import { FC } from 'react';
import { Icon, Text, Heading } from '@livechat/design-system-react-components';
import { CodeBlack as CodeBlackIcon, TextLogo } from '@livechat/design-system-icons';

import { buildOauthQuery } from 'helpers/oath-query-builder';
import { organizationIDEscaped } from 'helpers/account';
import Amplitude from 'services/Amplitude';
import { useProducts } from 'hooks/use-products';
import { getLogoSource } from 'helpers/logo';
import { getProductDisplayName } from 'helpers/product';
import { useGetMe } from 'hooks/use-get-me';
import { FilteredOrganization } from 'types/organization';
import { PRODUCTS } from 'constants/product';

import {
  productsContainerStyles,
  noProductsTextStyles,
  goToProductContainerStyles,
  linkStyles,
  goToProductStyles,
} from './styles';

type Props = {
  organization: FilteredOrganization;
  textPlatformAsProductEnabled: boolean;
};

export const OrganizationDetails: FC<Props> = ({ organization, textPlatformAsProductEnabled }) => {
  const { email } = useGetMe();
  const { getRedirectUrl, isFetchingProducts } = useProducts();

  const products = organization.products
    .filter(({ haveAccessToProduct }) => haveAccessToProduct)
    .map(({ name }) => name);
  const displayDeveloperConsole = products.includes('LiveChat') && !textPlatformAsProductEnabled;

  const devConsoleParam = {
    client_id: import.meta.env.VITE_APP_TP_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_APP_TP_APP_URL,
    response_type: 'token',
  };

  const panelParam = {
    client_id: import.meta.env.VITE_APP_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_APP_PANEL_URI,
    product: 'Accounts',
    response_type: 'token',
  };

  const filteredProducts = products.filter((product) => PRODUCTS.includes(product));

  const handleProductClick = (product: string) => {
    Amplitude.logEvent(email, 'AVATAR_MENU_ITEM_CLICK', { menuElement: product }, true);
  };

  if (isFetchingProducts) {
    return null;
  }

  return (
    <div css={productsContainerStyles} data-testid="organization-products">
      {filteredProducts.length === 0 ? (
        <Text css={noProductsTextStyles}>You don&apos;t have products in this organization</Text>
      ) : (
        <div>
          {filteredProducts.map((product) => (
            <a
              key={`${product}_${organization.id}`}
              href={getRedirectUrl(product, organization.id)}
              css={linkStyles}
              data-testid="product-link"
              onClick={() => handleProductClick(product)}
            >
              <div css={goToProductContainerStyles}>
                <Icon source={getLogoSource(product)} size="large" />
                <Heading size="xs" css={goToProductStyles}>
                  Go to {getProductDisplayName(product)}
                </Heading>
              </div>
            </a>
          ))}
          <div>
            <a
              css={linkStyles}
              href={`/?organization_id=${organizationIDEscaped(organization.id)}&${buildOauthQuery(panelParam)}`}
              data-testid="product-link"
              onClick={() => handleProductClick('Accounts')}
            >
              <div css={goToProductContainerStyles}>
                <Icon source={TextLogo} size="large" />
                <Heading size="xs" css={goToProductStyles}>
                  Go to Accounts
                </Heading>
              </div>
            </a>
          </div>
          <div>
            {displayDeveloperConsole && (
              <a
                css={linkStyles}
                href={`/?organization_id=${organizationIDEscaped(organization.id)}&${buildOauthQuery(
                  devConsoleParam
                )}`}
                data-testid="product-link"
                onClick={() => handleProductClick('Developer console')}
              >
                <div css={goToProductContainerStyles}>
                  <Icon source={CodeBlackIcon} size="large" />
                  <Heading size="xs" css={goToProductStyles}>
                    Developer console
                  </Heading>
                </div>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
